import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDay, faEnvelope, faSearch, faVideo } from '@fortawesome/free-solid-svg-icons';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, query, setDoc, Timestamp, where } from 'firebase/firestore';
import { getFirestore } from '@firebase/firestore';
import { app } from '../firebase.js';
import defaulProfPic from '../Images/defprofile.png';
import MaskedPhone from '../Components/MaskedPhone';
import { ORGANIZATION_NAME } from '../branding';
import { logAdminActivity } from '../utils/activityLogger';
import { buildInterviewInvitationEmail } from '../utils/emailTemplates';
import {
  buildGoogleCalendarLink,
  buildInterviewMeetingLink,
  getInterviewJoinAvailability,
  INTERVIEW_JOIN_EARLY_WINDOW_MINUTES,
  buildOutlookCalendarLink,
} from '../utils/interviewScheduling';

const DAILY_API_KEY = process.env.REACT_APP_DAILY_API_KEY || '9ca6078bdb4b21b14f89f74bf52454758a58e2488b6ce5a04e640014f9fc5c7c';
const DAILY_IFRAME_EXIT_PARAM = 'daily_iframe_exit';
const EMAIL_API_URL = 'https://us-central1-ubunifu-college.cloudfunctions.net/api/send-email';
const DEFAULT_INVITE_DURATION_MINUTES = 45;

const getStudentId = (student) => student?.userId || student?.id || '';
const getStudentDocId = (student) => student?.id || student?.userId || '';

const getDateObject = (value) => {
  if (!value) return null;
  const dateObj = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(dateObj.getTime())) return null;
  return dateObj;
};

const formatDateInputValue = (value) => {
  const dateObj = getDateObject(value);
  if (!dateObj) return '';

  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatTimeInputValue = (value) => {
  const dateObj = getDateObject(value);
  if (!dateObj) return '';

  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

const getDefaultScheduleStart = () => {
  const start = new Date();
  start.setDate(start.getDate() + 1);
  start.setHours(10, 0, 0, 0);
  return start;
};

const combineScheduleDateTime = (dateValue, timeValue) => {
  if (!dateValue || !timeValue) return null;
  const combined = new Date(`${dateValue}T${timeValue}:00`);
  if (Number.isNaN(combined.getTime())) return null;
  return combined;
};

const formatInviteDateTime = (value) => {
  const dateObj = getDateObject(value);
  if (!dateObj) return 'Not set';

  return dateObj.toLocaleString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'Africa/Nairobi',
    timeZoneName: 'short'
  });
};

const createMeetingId = (studentId) => {
  const safeStudentId = String(studentId || 'guest').replace(/[^a-zA-Z0-9]/g, '').slice(-6).toLowerCase() || 'guest';
  return `nda-${safeStudentId}-${Date.now().toString(36)}`;
};

const getStatusMeta = (callRecord) => {
  if (callRecord?.status === 'active' && callRecord?.roomUrl) {
    return { label: 'In progress', className: 'interviewStatusPill interviewStatusPill--ready' };
  }

  if (callRecord?.status === 'scheduled' && callRecord?.roomUrl) {
    return { label: 'Scheduled', className: 'interviewStatusPill interviewStatusPill--scheduled' };
  }

  if (callRecord?.status === 'ended') {
    return { label: 'Ended', className: 'interviewStatusPill interviewStatusPill--ended' };
  }

  if (callRecord?.roomUrl) {
    return { label: 'Room ready', className: 'interviewStatusPill interviewStatusPill--neutral' };
  }

  return { label: 'Not scheduled', className: 'interviewStatusPill interviewStatusPill--idle' };
};

const getBooleanStatusMeta = ({ isTrue, trueLabel, falseLabel, timestamp, formatDateTime }) => ({
  label: isTrue ? trueLabel : falseLabel,
  className: isTrue ? 'interviewJoinStatePill interviewJoinStatePill--yes' : 'interviewJoinStatePill interviewJoinStatePill--no',
  timestampLabel: timestamp ? formatDateTime(timestamp) : (isTrue ? 'Recorded' : 'Waiting')
});

const formatDurationLabel = (durationMs) => {
  const safeDurationMs = Math.max(0, Number(durationMs) || 0);
  const totalMinutes = Math.floor(safeDurationMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  if (totalMinutes > 0) {
    return `${totalMinutes} min`;
  }

  const totalSeconds = Math.floor(safeDurationMs / 1000);
  return totalSeconds > 0 ? `${totalSeconds} sec` : '0 min';
};

const getInterviewDurationMeta = ({ callRecord, currentTime }) => {
  const storedSeconds = Number(callRecord?.interviewDurationSeconds);
  if (Number.isFinite(storedSeconds) && storedSeconds >= 0) {
    return {
      label: formatDurationLabel(storedSeconds * 1000),
      hint: callRecord?.status === 'ended' ? 'Final duration' : 'Recorded duration'
    };
  }

  const startedAt = getDateObject(callRecord?.startedAt);
  if (!startedAt) {
    return {
      label: 'Not started',
      hint: 'Duration appears after the call begins'
    };
  }

  const endedAt = getDateObject(callRecord?.endedAt);
  const endDate = endedAt || currentTime;
  const durationMs = Math.max(0, endDate.getTime() - startedAt.getTime());

  return {
    label: formatDurationLabel(durationMs),
    hint: endedAt ? 'Final duration' : 'Live duration'
  };
};

function IntervComp() {
  const db = getFirestore(app);
  const auth = getAuth();
  const navigate = useNavigate();

  const [applicants, setApplicants] = useState([]);
  const [interviewCalls, setInterviewCalls] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [callLoadingById, setCallLoadingById] = useState({});
  const [activeInterviewRoom, setActiveInterviewRoom] = useState(null);
  const [currentAdminName, setCurrentAdminName] = useState('');
  const [currentAdminEmail, setCurrentAdminEmail] = useState('');
  const [endingInterview, setEndingInterview] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduleDuration, setScheduleDuration] = useState(DEFAULT_INVITE_DURATION_MINUTES);
  const [schedulingInvite, setSchedulingInvite] = useState(false);
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const lastSearchRef = useRef('');
  const interviewFrameRef = useRef(null);

  const formatDateTime = useCallback((value) => {
    const dateObj = getDateObject(value);
    if (!dateObj) return '—';

    const datePart = dateObj.toLocaleDateString();
    const timePart = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return `${datePart} • ${timePart}`;
  }, []);

  const fetchInterviewData = useCallback(async () => {
    setLoading(true);

    try {
      const [usersSnapshot, callsSnapshot] = await Promise.all([
        getDocs(query(collection(db, 'users'), where('admissionFeeStatus', '==', 'paid'))),
        getDocs(collection(db, 'interviewCalls'))
      ]);

      const paidApplicants = [];
      usersSnapshot.forEach((userDoc) => {
        const data = userDoc.data();
        const status = String(data.userStatus || '').trim().toLowerCase();

        if (status && status !== 'inactive') {
          return;
        }

        paidApplicants.push({ id: userDoc.id, userId: data.userId || userDoc.id, ...data });
      });

      paidApplicants.sort((firstApplicant, secondApplicant) => {
        const firstDate = getDateObject(firstApplicant.admissionFeePaidAt || firstApplicant.appDate)?.getTime() || 0;
        const secondDate = getDateObject(secondApplicant.admissionFeePaidAt || secondApplicant.appDate)?.getTime() || 0;
        return secondDate - firstDate;
      });

      const callMap = {};
      callsSnapshot.forEach((callDoc) => {
        callMap[callDoc.id] = callDoc.data();
      });

      setApplicants(paidApplicants);
      setInterviewCalls(callMap);
    } catch (error) {
      console.error('Error fetching interview applicants:', error);
      swal('Error', 'Failed to load paid admission-fee applicants for interviews.', 'error');
    } finally {
      setLoading(false);
    }
  }, [db]);

  useEffect(() => {
    fetchInterviewData();
  }, [fetchInterviewData]);

  useEffect(() => {
    const fetchCurrentAdmin = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setCurrentAdminName('');
        setCurrentAdminEmail('');
        return;
      }

      const fallbackName = currentUser.displayName || currentUser.email || 'Admin';
      const fallbackEmail = currentUser.email || '';

      try {
        const directDoc = await getDoc(doc(db, 'Admins', currentUser.uid));
        if (directDoc.exists()) {
          const adminData = directDoc.data();
          setCurrentAdminName(adminData.userName || fallbackName);
          setCurrentAdminEmail(adminData.userEmail || fallbackEmail);
          return;
        }

        const byUserId = await getDocs(query(collection(db, 'Admins'), where('userID', '==', currentUser.uid)));
        if (!byUserId.empty) {
          const adminData = byUserId.docs[0].data();
          setCurrentAdminName(adminData.userName || fallbackName);
          setCurrentAdminEmail(adminData.userEmail || fallbackEmail);
          return;
        }

        if (currentUser.email) {
          const byEmail = await getDocs(query(collection(db, 'Admins'), where('userEmail', '==', currentUser.email)));
          if (!byEmail.empty) {
            const adminData = byEmail.docs[0].data();
            setCurrentAdminName(adminData.userName || fallbackName);
            setCurrentAdminEmail(adminData.userEmail || fallbackEmail);
            return;
          }
        }

        setCurrentAdminName(fallbackName);
        setCurrentAdminEmail(fallbackEmail);
      } catch (error) {
        console.error('Failed to resolve current admin identity for interviews:', error);
        setCurrentAdminName(fallbackName);
        setCurrentAdminEmail(fallbackEmail);
      }
    };

    fetchCurrentAdmin();
  }, [auth, db]);

  useEffect(() => {
    const trimmed = searchTerm.trim();
    const timeoutId = setTimeout(() => {
      if (trimmed === lastSearchRef.current) return;
      lastSearchRef.current = trimmed;
      if (trimmed) {
        logAdminActivity({
          action: 'Search',
          details: `Interviews search: "${trimmed}"`,
          meta: { page: 'Interviews', query: trimmed }
        });
      }
    }, 600);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentTime(new Date());
    }, 30000);

    return () => window.clearInterval(intervalId);
  }, []);

  const filteredApplicants = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return applicants.filter((student) => {
      if (!search) {
        return true;
      }

      return [
        student.bio,
        student.course,
        student.nationality,
        student.phoneNumber,
        student.userEmail,
        student.prospectAdmissionNumber,
        student.admissionFeePaymentReference
      ].some((value) => String(value || '').toLowerCase().includes(search));
    });
  }, [applicants, searchTerm]);

  const uniqueCourses = useMemo(() => (
    Array.from(new Set(applicants.map((student) => student.course).filter(Boolean))).sort()
  ), [applicants]);

  const scheduledRoomsCount = useMemo(() => (
    applicants.filter((student) => {
      const call = interviewCalls[getStudentId(student)];
      return call?.status === 'scheduled' && call?.roomUrl;
    }).length
  ), [applicants, interviewCalls]);

  const activeRoomsCount = useMemo(() => (
    applicants.filter((student) => {
      const call = interviewCalls[getStudentId(student)];
      return call?.status === 'active' && call?.roomUrl;
    }).length
  ), [applicants, interviewCalls]);

  const viewStudent = useCallback((userId, studentName) => {
    logAdminActivity({
      action: 'View Student',
      details: `Opened interview candidate: ${studentName || 'student'}`,
      meta: { page: 'Interviews', studentId: userId, studentName: studentName || '' }
    });
    navigate('/view-student', { state: { myData: userId } });
  }, [navigate]);

  const openEmbeddedRoom = useCallback((student, roomData) => {
    const studentId = getStudentId(student);
    if (!studentId || !roomData?.roomUrl) {
      return;
    }

    setActiveInterviewRoom({
      studentId,
      studentName: student.bio || 'Applicant',
      course: student.course || '',
      nationality: student.nationality || '',
      roomName: roomData.roomName || '',
      roomUrl: roomData.roomUrl
    });
  }, []);

  const closeEmbeddedRoom = useCallback(() => {
    setActiveInterviewRoom(null);
  }, []);

  const createMeetingTokenUrl = useCallback(async (roomData, fallbackUserId) => {
    const currentUser = auth.currentUser;
    const roomName = roomData?.roomName;
    const roomUrl = roomData?.roomUrl;

    if (!roomName || !roomUrl || !currentUser) {
      return roomUrl || '';
    }

    const redirectBase = typeof window !== 'undefined' ? window.location.origin : '';
    const redirectPath = redirectBase
      ? `${redirectBase}/interviews?${DAILY_IFRAME_EXIT_PARAM}=1&studentId=${encodeURIComponent(fallbackUserId || currentUser.uid || '')}&roomName=${encodeURIComponent(roomName)}`
      : undefined;

    const response = await fetch('https://api.daily.co/v1/meeting-tokens', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${DAILY_API_KEY}`
      },
      body: JSON.stringify({
        properties: {
          room_name: roomName,
          is_owner: true,
          user_name: currentAdminName || currentUser.displayName || currentUser.email || 'Admin',
          user_id: currentUser.uid || fallbackUserId || roomName,
          enable_prejoin_ui: true,
          redirect_on_meeting_exit: redirectPath
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Daily meeting token creation failed with status ${response.status}`);
    }

    const tokenData = await response.json();
    if (!tokenData?.token) {
      return roomUrl;
    }

    return `${roomUrl}${roomUrl.includes('?') ? '&' : '?'}t=${encodeURIComponent(tokenData.token)}`;
  }, [auth, currentAdminName]);

  const createDailyRoom = useCallback(async ({ meetingId, expiresAt }) => {
    const expiryDate = getDateObject(expiresAt) || new Date(Date.now() + (4 * 60 * 60 * 1000));
    const expirySeconds = Math.floor(expiryDate.getTime() / 1000);

    const response = await fetch('https://api.daily.co/v1/rooms', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${DAILY_API_KEY}`
      },
      body: JSON.stringify({
        name: meetingId,
        properties: {
          exp: expirySeconds,
          eject_at_room_exp: true,
          enable_chat: true,
          enable_screenshare: true
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Daily room creation failed with status ${response.status}`);
    }

    const roomData = await response.json();
    return {
      roomName: roomData.name || meetingId,
      roomUrl: roomData.url || `https://${roomData.name || meetingId}.daily.co`
    };
  }, []);

  const sendInterviewEmail = useCallback(async ({ email, subject, message }) => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('Admin must be logged in to send interview invites.');
    }

    const token = await currentUser.getIdToken();
    const response = await fetch(EMAIL_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ email, subject, message })
    });

    if (!response.ok) {
      throw new Error(`Interview email failed with status ${response.status}`);
    }
  }, [auth]);

  const openScheduleInviteModal = useCallback((student) => {
    const studentId = getStudentId(student);
    const existingCall = interviewCalls[studentId];
    const scheduledStart = getDateObject(existingCall?.scheduledFor) || getDefaultScheduleStart();

    setSelectedApplicant(student);
    setScheduleDate(formatDateInputValue(scheduledStart));
    setScheduleTime(formatTimeInputValue(scheduledStart));
    setScheduleDuration(existingCall?.scheduledDurationMinutes || DEFAULT_INVITE_DURATION_MINUTES);
    setShowScheduleModal(true);
  }, [interviewCalls]);

  const closeScheduleInviteModal = useCallback(() => {
    setShowScheduleModal(false);
    setSelectedApplicant(null);
    setScheduleDate('');
    setScheduleTime('');
    setScheduleDuration(DEFAULT_INVITE_DURATION_MINUTES);
  }, []);

  const handleScheduleInvite = useCallback(async () => {
    if (!selectedApplicant) {
      return;
    }

    const studentId = getStudentId(selectedApplicant);
    if (!studentId) {
      swal('Error', 'This applicant record is missing an ID, so the meeting cannot be scheduled.', 'error');
      return;
    }

    if (!selectedApplicant.userEmail) {
      swal('Error', 'This applicant does not have an email address saved yet.', 'error');
      return;
    }

    if (!DAILY_API_KEY) {
      swal('Error', 'Daily API key is not configured for this app.', 'error');
      return;
    }

    const scheduledStart = combineScheduleDateTime(scheduleDate, scheduleTime);
    if (!scheduledStart) {
      swal('Error', 'Please choose a valid interview date and time.', 'error');
      return;
    }

    if (scheduledStart.getTime() <= Date.now()) {
      swal('Error', 'Please choose a future interview date and time.', 'error');
      return;
    }

    const durationMinutes = Math.max(15, Number(scheduleDuration) || DEFAULT_INVITE_DURATION_MINUTES);
    const scheduledEnd = new Date(scheduledStart.getTime() + (durationMinutes * 60 * 1000));
    const existingCall = interviewCalls[studentId];

    setSchedulingInvite(true);

    try {
      let meetingId = existingCall?.meetingId || existingCall?.roomName || '';
      let roomName = existingCall?.roomName || '';
      let roomUrl = existingCall?.roomUrl || '';

      if (!roomUrl || existingCall?.status === 'ended') {
        meetingId = createMeetingId(studentId);
        const createdRoom = await createDailyRoom({
          meetingId,
          expiresAt: new Date(scheduledEnd.getTime() + (8 * 60 * 60 * 1000))
        });

        roomName = createdRoom.roomName;
        roomUrl = createdRoom.roomUrl;
        meetingId = createdRoom.roomName || meetingId;
      }

      const meetingLink = buildInterviewMeetingLink(meetingId);
      const calendarDetails = [
        `${ORGANIZATION_NAME} admissions interview for ${selectedApplicant.bio || 'Applicant'}.`,
        `Join link: ${meetingLink}`,
        `Course: ${selectedApplicant.course || 'Course not set'}`
      ].join('\n');

      const googleCalendarLink = buildGoogleCalendarLink({
        title: `${ORGANIZATION_NAME} interview - ${selectedApplicant.bio || 'Applicant'}`,
        start: scheduledStart,
        end: scheduledEnd,
        details: calendarDetails,
        location: meetingLink
      });

      const outlookCalendarLink = buildOutlookCalendarLink({
        title: `${ORGANIZATION_NAME} interview - ${selectedApplicant.bio || 'Applicant'}`,
        start: scheduledStart,
        end: scheduledEnd,
        details: calendarDetails,
        location: meetingLink
      });

      const now = Timestamp.now();
      const callRecord = {
        userId: studentId,
        studentName: selectedApplicant.bio || '',
        studentEmail: selectedApplicant.userEmail || '',
        nationality: selectedApplicant.nationality || '',
        course: selectedApplicant.course || '',
        roomName,
        roomUrl,
        meetingId,
        meetingLink,
        adminName: currentAdminName || auth.currentUser?.displayName || auth.currentUser?.email || 'Admin',
        adminEmail: currentAdminEmail || auth.currentUser?.email || '',
        scheduledFor: Timestamp.fromDate(scheduledStart),
        scheduledEndsAt: Timestamp.fromDate(scheduledEnd),
        scheduledDurationMinutes: durationMinutes,
        status: 'scheduled',
        inviteSentAt: now,
        updatedAt: now,
        lastInvitedBy: auth.currentUser?.uid || null,
        createdBy: existingCall?.createdBy || auth.currentUser?.uid || null
      };

      await setDoc(doc(db, 'interviewCalls', studentId), callRecord, { merge: true });
      setInterviewCalls((prev) => ({
        ...prev,
        [studentId]: {
          ...prev[studentId],
          ...callRecord
        }
      }));

      const studentEmailMessage = buildInterviewInvitationEmail({
        recipientName: selectedApplicant.bio,
        studentName: selectedApplicant.bio,
        adminName: currentAdminName || auth.currentUser?.displayName || auth.currentUser?.email || 'Admissions team',
        scheduledStart,
        scheduledEnd,
        meetingLink,
        googleCalendarLink,
        outlookCalendarLink,
        course: selectedApplicant.course,
      });

      const adminRecipient = currentAdminEmail || auth.currentUser?.email || '';
      const adminEmailMessage = buildInterviewInvitationEmail({
        recipientName: currentAdminName || auth.currentUser?.displayName || 'Admin',
        studentName: selectedApplicant.bio,
        adminName: currentAdminName || auth.currentUser?.displayName || auth.currentUser?.email || 'Admin',
        scheduledStart,
        scheduledEnd,
        meetingLink,
        googleCalendarLink,
        outlookCalendarLink,
        course: selectedApplicant.course,
        isAdminCopy: true
      });

      await sendInterviewEmail({
        email: selectedApplicant.userEmail,
        subject: `${ORGANIZATION_NAME} interview invitation`,
        message: studentEmailMessage
      });

      if (adminRecipient) {
        sendInterviewEmail({
          email: adminRecipient,
          subject: `Interview scheduled for ${selectedApplicant.bio || 'applicant'}`,
          message: adminEmailMessage
        }).catch((adminEmailError) => {
          console.error('Admin copy for interview invite failed:', adminEmailError);
        });
      }

      await setDoc(doc(db, 'users', getStudentDocId(selectedApplicant) || studentId), {
        applicationStage: 'Interview Invite Sent',
        interviewStatus: 'scheduled',
        interviewScheduledFor: Timestamp.fromDate(scheduledStart),
        interviewScheduledEndsAt: Timestamp.fromDate(scheduledEnd),
        interviewInviteSentAt: now,
        interviewInviteEmailStatus: 'sent',
        interviewInviteEmailError: '',
        interviewMeetingId: meetingId,
        interviewMeetingLink: meetingLink,
        interviewScheduledBy: auth.currentUser?.uid || null,
        interviewScheduledByEmail: currentAdminEmail || auth.currentUser?.email || '',
        updatedAt: now
      }, { merge: true });

      logAdminActivity({
        action: existingCall?.scheduledFor ? 'Resend Interview Invite' : 'Schedule Interview Invite',
        details: `Scheduled interview invite for ${selectedApplicant.bio || 'student'}`,
        meta: {
          page: 'Interviews',
          studentId,
          studentName: selectedApplicant.bio || '',
          meetingId,
          scheduledFor: scheduledStart.toISOString()
        }
      });

      closeScheduleInviteModal();
      swal('Invite sent', `The interview invite was emailed with a join link and calendar options for ${selectedApplicant.bio || 'the applicant'}.`, 'success');
    } catch (error) {
      console.error('Error scheduling interview invite:', error);
      swal('Error', 'Failed to schedule and send the interview invite. Please try again.', 'error');
    } finally {
      setSchedulingInvite(false);
    }
  }, [
    auth,
    closeScheduleInviteModal,
    createDailyRoom,
    currentAdminEmail,
    currentAdminName,
    db,
    interviewCalls,
    scheduleDate,
    scheduleDuration,
    scheduleTime,
    selectedApplicant,
    sendInterviewEmail
  ]);

  const completeInterviewEnd = useCallback(async ({ confirmUser = true, overrideRoom } = {}) => {
    const roomToEnd = overrideRoom || activeInterviewRoom;
    const persistedCallRecord = roomToEnd?.studentId ? interviewCalls[roomToEnd.studentId] : null;

    if (!roomToEnd?.studentId || !roomToEnd?.roomName) {
      closeEmbeddedRoom();
      return;
    }

    if (confirmUser) {
      const shouldEnd = await swal({
        title: 'End interview?',
        text: 'This will close the call view, mark the interview as ended, and delete the Daily room.',
        icon: 'warning',
        buttons: ['Cancel', 'End interview'],
        dangerMode: true,
      });

      if (!shouldEnd) {
        return;
      }
    }

    setEndingInterview(true);

    try {
      const deleteResponse = await fetch(`https://api.daily.co/v1/rooms/${encodeURIComponent(roomToEnd.roomName)}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${DAILY_API_KEY}`
        }
      });

      if (!deleteResponse.ok && deleteResponse.status !== 404) {
        throw new Error(`Daily room deletion failed with status ${deleteResponse.status}`);
      }

      const now = Timestamp.now();
      const startedAt = getDateObject(roomToEnd.startedAt || persistedCallRecord?.startedAt);
      const endedAtDate = now.toDate();
      const interviewDurationSeconds = startedAt
        ? Math.max(0, Math.floor((endedAtDate.getTime() - startedAt.getTime()) / 1000))
        : 0;
      const interviewDurationMinutes = Number((interviewDurationSeconds / 60).toFixed(1));
      await setDoc(doc(db, 'interviewCalls', roomToEnd.studentId), {
        status: 'ended',
        endedAt: now,
        interviewDurationSeconds,
        interviewDurationMinutes,
        updatedAt: now,
        endedBy: auth.currentUser?.uid || null
      }, { merge: true });

      await setDoc(doc(db, 'users', roomToEnd.studentId), {
        applicationStage: 'Interview Completed',
        interviewStatus: 'completed',
        interviewCompletedAt: now,
        interviewDurationMinutes,
        interviewCompletedBy: auth.currentUser?.uid || null,
        interviewCompletedByEmail: auth.currentUser?.email || '',
        updatedAt: now
      }, { merge: true });

      setInterviewCalls((prev) => ({
        ...prev,
        [roomToEnd.studentId]: {
          ...prev[roomToEnd.studentId],
          status: 'ended',
          endedAt: now,
          interviewDurationSeconds,
          interviewDurationMinutes,
          updatedAt: now,
          endedBy: auth.currentUser?.uid || null
        }
      }));

      logAdminActivity({
        action: 'End Interview Call',
        details: `Ended Daily room for ${roomToEnd.studentName || 'student'}`,
        meta: {
          page: 'Interviews',
          studentId: roomToEnd.studentId,
          studentName: roomToEnd.studentName || '',
          roomName: roomToEnd.roomName
        }
      });

      closeEmbeddedRoom();
      if (confirmUser) {
        swal('Ended', 'The interview call has been closed and the Daily room deleted.', 'success');
      }
    } catch (error) {
      console.error('Error ending Daily interview call:', error);
      swal('Error', 'Failed to end the interview cleanly. Please try again.', 'error');
    } finally {
      setEndingInterview(false);
    }
  }, [activeInterviewRoom, auth, closeEmbeddedRoom, db, interviewCalls]);

  const handleInterviewCall = useCallback(async (student) => {
    const studentId = getStudentId(student);
    if (!studentId) {
      swal('Error', 'This applicant record is missing an ID, so the interview room cannot be created.', 'error');
      return;
    }

    if (!DAILY_API_KEY) {
      swal('Error', 'Daily API key is not configured for this app.', 'error');
      return;
    }

    setCallLoadingById((prev) => ({ ...prev, [studentId]: true }));

    try {
      const existingCall = interviewCalls[studentId];
      const joinAvailability = existingCall?.status === 'scheduled'
        ? getInterviewJoinAvailability(existingCall?.scheduledFor, new Date())
        : { canJoin: true, availableAt: null };

      if (existingCall?.status === 'scheduled' && !joinAvailability.canJoin) {
        swal(
          'Not open yet',
          `This scheduled interview can be started ${INTERVIEW_JOIN_EARLY_WINDOW_MINUTES} minutes before the meeting time. It opens at ${formatInviteDateTime(joinAvailability.availableAt)}.`,
          'info'
        );
        return;
      }

      let callRecord = existingCall;

      if (!callRecord?.roomUrl || callRecord?.status === 'ended') {
        const scheduledEndDate = getDateObject(callRecord?.scheduledEndsAt);
        const createdRoom = await createDailyRoom({
          meetingId: createMeetingId(studentId),
          expiresAt: scheduledEndDate ? new Date(scheduledEndDate.getTime() + (8 * 60 * 60 * 1000)) : undefined
        });

        callRecord = {
          ...callRecord,
          userId: studentId,
          studentName: student.bio || '',
          studentEmail: student.userEmail || '',
          nationality: student.nationality || '',
          course: student.course || '',
          meetingId: createdRoom.roomName,
          roomName: createdRoom.roomName,
          roomUrl: createdRoom.roomUrl
        };
      }

      const now = Timestamp.now();
      const nextStatus = 'active';
      const nextCallRecord = {
        ...callRecord,
        userId: studentId,
        studentName: student.bio || '',
        studentEmail: student.userEmail || '',
        nationality: student.nationality || '',
        course: student.course || '',
        meetingId: callRecord.meetingId || callRecord.roomName,
        meetingLink: buildInterviewMeetingLink(callRecord.meetingId || callRecord.roomName),
        status: nextStatus,
        startedAt: callRecord.startedAt || now,
        hostJoinedAt: callRecord.hostJoinedAt || now,
        hostJoinedBy: auth.currentUser?.uid || null,
        hostJoinedName: currentAdminName || auth.currentUser?.displayName || auth.currentUser?.email || 'Admin',
        hostJoinedEmail: currentAdminEmail || auth.currentUser?.email || '',
        bothJoinedAt: callRecord.bothJoinedAt || (callRecord?.studentJoinedAt ? now : null),
        lastJoinedAt: now,
        lastJoinedBy: auth.currentUser?.uid || null,
        updatedAt: now,
        createdBy: callRecord.createdBy || auth.currentUser?.uid || null
      };

      await setDoc(doc(db, 'interviewCalls', studentId), nextCallRecord, { merge: true });
      setInterviewCalls((prev) => ({
        ...prev,
        [studentId]: {
          ...prev[studentId],
          ...nextCallRecord
        }
      }));

      logAdminActivity({
        action: existingCall?.status === 'scheduled' ? 'Start Scheduled Interview' : (existingCall?.roomUrl ? 'Join Interview Call' : 'Start Interview Call'),
        details: `${existingCall?.status === 'scheduled' ? 'Started scheduled Daily room for' : 'Joined Daily room for'} ${student.bio || 'student'}`,
        meta: {
          page: 'Interviews',
          studentId,
          studentName: student.bio || '',
          roomUrl: nextCallRecord.roomUrl
        }
      });

      try {
        const tokenizedRoomUrl = await createMeetingTokenUrl(nextCallRecord, studentId);
        openEmbeddedRoom(student, { ...nextCallRecord, roomUrl: tokenizedRoomUrl || nextCallRecord.roomUrl });
      } catch (tokenError) {
        console.error('Error creating Daily token for interview room:', tokenError);
        openEmbeddedRoom(student, nextCallRecord);
      }
    } catch (error) {
      console.error('Error starting Daily interview call:', error);
      swal('Error', 'Failed to start the video call. Please try again.', 'error');
    } finally {
      setCallLoadingById((prev) => ({ ...prev, [studentId]: false }));
    }
  }, [auth, createDailyRoom, createMeetingTokenUrl, currentAdminEmail, currentAdminName, db, formatInviteDateTime, interviewCalls, openEmbeddedRoom]);

  const handleEndInterviewCall = useCallback(async () => {
    await completeInterviewEnd({ confirmUser: true });
  }, [completeInterviewEnd]);

  const handleInterviewFrameLoad = useCallback(async () => {
    if (!interviewFrameRef.current || endingInterview) {
      return;
    }

    try {
      const frameLocation = interviewFrameRef.current.contentWindow?.location;
      if (!frameLocation?.href) {
        return;
      }

      const parsedUrl = new URL(frameLocation.href);
      if (parsedUrl.searchParams.get(DAILY_IFRAME_EXIT_PARAM) !== '1') {
        return;
      }

      await completeInterviewEnd({ confirmUser: false });
    } catch (error) {
      // Cross-origin while Daily is loaded; this only becomes readable after redirecting back to our app origin.
    }
  }, [completeInterviewEnd, endingInterview]);

  const selectedApplicantCall = selectedApplicant ? interviewCalls[getStudentId(selectedApplicant)] : null;
  const previewMeetingId = selectedApplicantCall?.meetingId || selectedApplicantCall?.roomName || '';
  const previewMeetingLink = previewMeetingId ? buildInterviewMeetingLink(previewMeetingId) : '';
  const previewScheduleStart = combineScheduleDateTime(scheduleDate, scheduleTime);
  const previewScheduleEnd = previewScheduleStart
    ? new Date(previewScheduleStart.getTime() + ((Number(scheduleDuration) || DEFAULT_INVITE_DURATION_MINUTES) * 60 * 1000))
    : null;

  return (
    <div className="clientsComp applicationsPage interviewsPage">
      <div className="appsHeader">
        <div>
          <p className="appsKicker">Admissions</p>
          <h4>Admission Interviews</h4>
          <p className="interviewCardsSubtext">Applicants appear here after their admission fee is recorded as paid.</p>
        </div>
        <div className="appsHeaderStats">
          <div className="appsStatChip">
            <span>Paid applicants</span>
            <strong>{applicants.length}</strong>
          </div>
          <div className="appsStatChip">
            <span>Courses</span>
            <strong>{uniqueCourses.length}</strong>
          </div>
          <div className="appsStatChip">
            <span>Scheduled</span>
            <strong>{scheduledRoomsCount}</strong>
          </div>
          <div className="appsStatChip">
            <span>Live now</span>
            <strong>{activeRoomsCount}</strong>
          </div>
        </div>
      </div>

      <div className="appsFiltersCard interviewsFiltersCard">
        <div className="appsSearch input-group">
          <span className="input-group-text"><FontAwesomeIcon icon={faSearch} /></span>
          <Form.Control
            type="text"
            placeholder="Search name, admission ref, course, phone..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>
        <Button variant="outline-dark" className="interviewsRefreshBtn" onClick={fetchInterviewData}>
          Refresh
        </Button>
      </div>

      {activeInterviewRoom && (
        <div className="interviewEmbedCard">
          <div className="interviewEmbedHeader">
            <div>
              <p className="interviewEmbedKicker">Live Interview</p>
              <h5>{activeInterviewRoom.studentName}</h5>
              <div className="interviewEmbedMeta">
                <span>{activeInterviewRoom.course || 'Course not set'}</span>
                <span>{activeInterviewRoom.nationality || 'Country not set'}</span>
                <span>{activeInterviewRoom.roomName || 'Daily room'}</span>
              </div>
            </div>
            <div className="interviewEmbedActions">
              <button
                className="appsViewBtn"
                onClick={() => viewStudent(activeInterviewRoom.studentId, activeInterviewRoom.studentName)}
              >
                View applicant
              </button>
              <button
                className="interviewCloseBtn"
                onClick={handleEndInterviewCall}
                disabled={endingInterview}
              >
                {endingInterview ? 'Ending...' : 'End interview'}
              </button>
            </div>
          </div>

          <div className="interviewEmbedFrameWrap">
            <iframe
              ref={interviewFrameRef}
              key={activeInterviewRoom.roomUrl}
              src={activeInterviewRoom.roomUrl}
              title={`Interview room for ${activeInterviewRoom.studentName}`}
              className="interviewEmbedFrame"
              allow="camera; microphone; fullscreen; speaker-selection; display-capture; autoplay; clipboard-write"
              onLoad={handleInterviewFrameLoad}
            />
          </div>
        </div>
      )}

      <div className="appsTableCard interviewCardsSection">
        <div className="appsTableHeader interviewCardsHeader">
          <div>
            <h6>Interview Invite Queue</h6>
            <p className="interviewCardsSubtext">Recent applicants who have paid the admission fee and are waiting for interview scheduling.</p>
          </div>
          <span>{filteredApplicants.length} results</span>
        </div>

        {loading ? (
          <div className="interviewsLoadingState">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : filteredApplicants.length === 0 ? (
          <div className="appsEmptyState interviewCardsEmptyState">
            No paid admission-fee applicants match your search right now.
          </div>
        ) : (
          <div className="interviewCardsGrid">
            {filteredApplicants.map((student) => {
              const studentId = getStudentId(student);
              const callRecord = interviewCalls[studentId];
              const statusMeta = getStatusMeta(callRecord);
              const interviewCompletedAt = callRecord?.endedAt || student?.interviewCompletedAt || null;
              const interviewDoneMeta = getBooleanStatusMeta({
                isTrue: callRecord?.status === 'ended' || student?.interviewStatus === 'completed' || !!interviewCompletedAt,
                trueLabel: 'Done',
                falseLabel: 'Pending',
                timestamp: interviewCompletedAt,
                formatDateTime
              });
              const hostJoinedMeta = getBooleanStatusMeta({
                isTrue: !!callRecord?.hostJoinedAt,
                trueLabel: 'Joined',
                falseLabel: 'Not yet',
                timestamp: callRecord?.hostJoinedAt,
                formatDateTime
              });
              const studentJoinedMeta = getBooleanStatusMeta({
                isTrue: !!callRecord?.studentJoinedAt,
                trueLabel: 'Joined',
                falseLabel: 'Not yet',
                timestamp: callRecord?.studentJoinedAt,
                formatDateTime
              });
              const bothJoinedAt = callRecord?.bothJoinedAt || ((callRecord?.hostJoinedAt && callRecord?.studentJoinedAt) ? (callRecord?.studentJoinedAt || callRecord?.hostJoinedAt) : null);
              const bothJoinedMeta = getBooleanStatusMeta({
                isTrue: !!bothJoinedAt,
                trueLabel: 'Confirmed',
                falseLabel: 'Waiting',
                timestamp: bothJoinedAt,
                formatDateTime
              });
              const interviewDurationMeta = getInterviewDurationMeta({
                callRecord,
                currentTime
              });
              const joinAvailability = callRecord?.status === 'scheduled'
                ? getInterviewJoinAvailability(callRecord?.scheduledFor, currentTime)
                : { canJoin: true, availableAt: null };
              const joinLabel = callRecord?.status === 'scheduled' && callRecord?.roomUrl
                ? (joinAvailability.canJoin ? 'Start call' : 'Starts soon')
                : (callRecord?.roomUrl && callRecord?.status !== 'ended' ? 'Join call' : 'Start call');

              return (
                <article key={student.id} className="interviewApplicantCard">
                  <div className="interviewApplicantCardTop">
                    <div className="appsStudentCell">
                      <div className="appDefProfPic">
                        <img src={student.profilePic ? student.profilePic : defaulProfPic} alt="profile" />
                      </div>
                      <div>
                        <p className="appsStudentName">{student.bio || 'Unnamed applicant'}</p>
                        <span className="appsStudentMeta">{student.userEmail || 'Email not set'}</span>
                      </div>
                    </div>
                    <span className={statusMeta.className}>
                      {statusMeta.label}
                    </span>
                  </div>

                  <div className="interviewApplicantTags">
                    <span className="interviewCountryPill">{student.prospectAdmissionNumber || 'Admission ref pending'}</span>
                    <span className="interviewMetaTag">{student.course || 'Course not set'}</span>
                    <span className="interviewMetaTag">{student.nationality || 'Country not set'}</span>
                  </div>

                  <div className="interviewApplicantMetaGrid">
                    <div className="interviewApplicantMetaCard">
                      <span>Admission fee paid</span>
                      <strong>{formatDateTime(student.admissionFeePaidAt)}</strong>
                    </div>
                    <div className="interviewApplicantMetaCard">
                      <span>Interview date</span>
                      <strong>{formatDateTime(callRecord?.scheduledFor || callRecord?.startedAt)}</strong>
                    </div>
                    <div className="interviewApplicantMetaCard">
                      <span>Phone</span>
                      <strong>
                        <MaskedPhone
                          phoneNumber={student.phoneNumber}
                          studentId={studentId}
                          studentName={student.bio}
                        />
                      </strong>
                    </div>
                    <div className="interviewApplicantMetaCard">
                      <span>Meeting</span>
                      <strong>{callRecord?.meetingId || callRecord?.roomName || 'No meeting link yet'}</strong>
                    </div>
                    <div className="interviewApplicantMetaCard">
                      <span>Payment ref</span>
                      <strong>{student.admissionFeePaymentReference || student.admissionFeePaymentId || 'No payment ref saved'}</strong>
                    </div>
                    <div className="interviewApplicantMetaCard">
                      <span>Host joined</span>
                      <strong className={hostJoinedMeta.className}>{hostJoinedMeta.label}</strong>
                      <small>{hostJoinedMeta.timestampLabel}</small>
                    </div>
                    <div className="interviewApplicantMetaCard">
                      <span>Student joined</span>
                      <strong className={studentJoinedMeta.className}>{studentJoinedMeta.label}</strong>
                      <small>{studentJoinedMeta.timestampLabel}</small>
                    </div>
                    <div className="interviewApplicantMetaCard">
                      <span>Both joined</span>
                      <strong className={bothJoinedMeta.className}>{bothJoinedMeta.label}</strong>
                      <small>{bothJoinedMeta.timestampLabel}</small>
                    </div>
                    <div className="interviewApplicantMetaCard">
                      <span>Interview done</span>
                      <strong className={interviewDoneMeta.className}>{interviewDoneMeta.label}</strong>
                      <small>{interviewDoneMeta.timestampLabel}</small>
                    </div>
                    <div className="interviewApplicantMetaCard">
                      <span>Meeting duration</span>
                      <strong>{interviewDurationMeta.label}</strong>
                      <small>{interviewDurationMeta.hint}</small>
                    </div>
                  </div>

                  <div className="interviewApplicantActions">
                    <button
                      className="interviewScheduleBtn"
                      onClick={() => openScheduleInviteModal(student)}
                    >
                      <FontAwesomeIcon icon={callRecord?.scheduledFor ? faEnvelope : faCalendarDay} />
                      <span>{callRecord?.scheduledFor ? 'Resend invite' : 'Send invite'}</span>
                    </button>
                    <button
                      className="appsViewBtn interviewCardViewBtn"
                      onClick={() => viewStudent(studentId, student.bio)}
                    >
                      View applicant
                    </button>
                    <button
                      className="interviewCallBtn"
                      onClick={() => handleInterviewCall(student)}
                      disabled={!studentId || !!callLoadingById[studentId] || (callRecord?.status === 'scheduled' && !joinAvailability.canJoin)}
                    >
                      {callLoadingById[studentId] ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                          <span>Preparing...</span>
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faVideo} />
                          <span>{joinLabel}</span>
                        </>
                      )}
                    </button>
                  </div>

                  {callRecord?.status === 'scheduled' && !joinAvailability.canJoin && (
                    <p className="interviewJoinNotice">
                      This call opens at {formatInviteDateTime(joinAvailability.availableAt)}.
                    </p>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>

      <Modal
        show={showScheduleModal}
        onHide={schedulingInvite ? undefined : closeScheduleInviteModal}
        centered
        dialogClassName="interviewScheduleModal"
      >
        <Modal.Header closeButton={!schedulingInvite}>
          <Modal.Title>Send interview invite</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="interviewScheduleSummary">
            <p className="interviewScheduleKicker">Applicant</p>
            <h6>{selectedApplicant?.bio || 'Applicant'}</h6>
            <span>{selectedApplicant?.userEmail || 'Email not set'}</span>
          </div>

          <div className="interviewScheduleGrid">
            <Form.Group>
              <Form.Label>Date</Form.Label>
              <Form.Control
                type="date"
                value={scheduleDate}
                onChange={(event) => setScheduleDate(event.target.value)}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Time</Form.Label>
              <Form.Control
                type="time"
                value={scheduleTime}
                onChange={(event) => setScheduleTime(event.target.value)}
              />
            </Form.Group>
            <Form.Group className="interviewScheduleDurationField">
              <Form.Label>Duration</Form.Label>
              <Form.Select
                value={scheduleDuration}
                onChange={(event) => setScheduleDuration(event.target.value)}
              >
                <option value="30">30 minutes</option>
                <option value="45">45 minutes</option>
                <option value="60">60 minutes</option>
                <option value="90">90 minutes</option>
              </Form.Select>
            </Form.Group>
          </div>

          <div className="interviewSchedulePreview">
            <p className="interviewSchedulePreviewLabel">What will be sent</p>
            <div className="interviewSchedulePreviewRow">
              <span>Meeting link</span>
              <strong>{previewMeetingLink || 'Link will be generated when the invite is sent.'}</strong>
            </div>
            <div className="interviewSchedulePreviewRow">
              <span>Scheduled for</span>
              <strong>{previewScheduleStart ? formatInviteDateTime(previewScheduleStart) : 'Choose date and time'}</strong>
            </div>
            <div className="interviewSchedulePreviewRow">
              <span>Calendar links</span>
              <strong>{previewScheduleStart && previewScheduleEnd ? 'Google Calendar and Outlook buttons will be included.' : 'Set a valid date to include calendar options.'}</strong>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={closeScheduleInviteModal} disabled={schedulingInvite}>
            Cancel
          </Button>
          <Button variant="dark" onClick={handleScheduleInvite} disabled={schedulingInvite}>
            {schedulingInvite ? 'Sending invite...' : 'Send invite'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default IntervComp;
